import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { combineLatest, map } from 'rxjs';
import { ReportTimezoneService, ReportZone } from '../../../../core/http/report-timezone.service';
import { DateTimeOptionQueryParams, DateTimeOptionValue } from '../../date-time-selector/date-time-selector.model';
import { DateTimeSelectorService } from '../../date-time-selector/date-time-selector.service';
import { UserService } from '../../services/user.service';

const TOAST_KEY = 'report-timezone';

function timeRangeKey({ interval, startDate, endDate }: DateTimeOptionQueryParams): string {
    return interval === DateTimeOptionValue.CUSTOM ? `${interval}:${startDate}:${endDate}` : interval;
}

/**
 * Has its own toast container, so the notice can be closed without clearing the other app toasts.
 */
@Component({
    selector: 'zp-report-timezone-notification',
    template: '<p-toast [key]="toastKey" position="top-center" />',
    standalone: true,
    imports: [ToastModule]
})
export class ReportTimezoneNotificationComponent {

    protected readonly toastKey = TOAST_KEY;
    private readonly messageService = inject(MessageService);
    private readonly dateTimeSelectorService = inject(DateTimeSelectorService);
    // the range the toast was last shown for, so reloading its report or selecting it again doesn't repeat the toast
    private notifiedTimeRange: string | null = null;

    constructor() {
        const userTimezone$ = inject(UserService).user$.pipe(map(user => user.publisherDetails.userInfo.timezone));
        combineLatest([inject(ReportTimezoneService).reportZone$, userTimezone$])
            .pipe(takeUntilDestroyed())
            .subscribe(([reportZone, userTimezone]) => {
                if (!reportZone) {
                    return;
                }
                if (reportZone.timezone === userTimezone) {
                    this.messageService.clear(TOAST_KEY);
                    this.notifiedTimeRange = null;
                    return;
                }
                this.notifyOnTimeRangeChange(reportZone);
            });
    }

    private notifyOnTimeRangeChange(reportZone: ReportZone): void {
        const key = timeRangeKey(this.dateTimeSelectorService.dateTimeOptionQueryParams.value);
        if (key === this.notifiedTimeRange) {
            return;
        }
        this.notifiedTimeRange = key;
        this.messageService.clear(TOAST_KEY);
        this.messageService.add({
            key: TOAST_KEY,
            severity: 'warn',
            sticky: true,
            summary: 'Report timezone changed',
            detail: 'Reports whose range starts before the last 90 days (today included) are computed in UTC. '
                + `This report is shown in ${reportZone.name}.`
        });
    }
}
